import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Volume2, Sparkles, MapPin, Loader2, Check, X, Crown, Lock } from "lucide-react";
import { toast } from "sonner";

function speak(text, lang) {
  if (!("speechSynthesis" in window)) return;
  const langMap = {
    spanish: "es-ES", french: "fr-FR", german: "de-DE",
    italian: "it-IT", japanese: "ja-JP", portuguese: "pt-PT",
    korean: "ko-KR", mandarin: "zh-CN",
  };
  const u = new SpeechSynthesisUtterance(text);
  u.lang = langMap[lang] || "en-US";
  u.rate = 0.85;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

export default function LessonDialog({ isoDate, language, isPro, onClose, onUpgrade }) {
  const { user, setUser } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [paywalled, setPaywalled] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [completing, setCompleting] = useState(false);

  const isCompleted = user?.completed_dates?.includes(isoDate);
  const isFuture = isoDate > new Date().toISOString().slice(0, 10);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(""); setLesson(null); setPaywalled(false);
    setSelectedOpt(null); setSubmitted(false);
    api.get(`/lessons/${language}/${isoDate}`)
      .then((r) => { if (alive) setLesson(r.data.lesson); })
      .catch((e) => {
        if (!alive) return;
        if (e.response?.status === 402) {
          setPaywalled(true);
        } else {
          setErr(e.response?.data?.detail || "Couldn't load lesson");
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isoDate, language]);

  const correctIdx = lesson?.quiz?.correct_index;
  const isCorrect = submitted && selectedOpt === correctIdx;

  const submitQuiz = () => {
    if (selectedOpt == null) return;
    setSubmitted(true);
  };

  const markComplete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post("/lessons/complete", {
        language, date: isoDate, quiz_correct: isCorrect,
      });
      setUser(data.user);
      toast.success(
        data.was_already_completed
          ? "Already saved — streak stays lit."
          : `+1 day! Streak: ${data.user.streak} 🔥`,
      );
      onClose();
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.detail === "streak_paywall") {
        setPaywalled("streak");
      } else {
        toast.error(e.response?.data?.detail || "Could not save");
      }
    } finally {
      setCompleting(false);
    }
  };

  const readableDate = new Date(isoDate + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg bg-card border rounded-2xl p-0 overflow-hidden animate-slide-up"
        data-testid="lesson-dialog"
      >
        <div className="bg-secondary text-secondary-foreground px-6 py-5">
          <DialogHeader>
            <div className="text-xs uppercase tracking-widest text-secondary-foreground/70">
              {readableDate}
            </div>
            <DialogTitle className="font-display text-2xl mt-1 text-secondary-foreground">
              Today's word
            </DialogTitle>
            <DialogDescription className="text-secondary-foreground/70 sr-only">
              Daily language lesson
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Preparing your lesson…
            </div>
          )}
          {err && <div className="text-destructive text-sm">{err}</div>}

          {paywalled && !loading && (
            <div className="text-center py-6 space-y-4" data-testid="lesson-paywall">
              <div className="w-14 h-14 rounded-full bg-terracotta/10 text-terracotta mx-auto flex items-center justify-center">
                <Lock className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-2xl tracking-tight">
                {paywalled === "streak"
                  ? "You just hit a 5-day streak! 🔥"
                  : "This lesson is locked"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {paywalled === "streak"
                  ? "Free rides go five days deep. Upgrade to keep the fire alive, unlock every language, and every day you've missed."
                  : "Free covers today plus the last 7 days. Pro unlocks every day you've missed — in every language."}
              </p>
              <Button
                onClick={onUpgrade}
                data-testid="paywall-upgrade-button"
                className="rounded-full h-11 px-6 bg-terracotta hover:bg-terracotta/90 text-white"
              >
                <Crown className="w-4 h-4 mr-2" strokeWidth={2} />
                {paywalled === "streak" ? "Keep the streak alive" : "See Pro plans"}
              </Button>
            </div>
          )}

          {lesson && !loading && !paywalled && (
            <>
              {/* Word block */}
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-4xl tracking-tight" data-testid="lesson-word">
                      {lesson.word}
                    </div>
                    <div className="text-sm text-muted-foreground italic mt-1">
                      /{lesson.pronunciation}/ · {lesson.part_of_speech}
                    </div>
                  </div>
                  <Button
                    variant="outline" size="icon"
                    onClick={() => speak(lesson.word, language)}
                    data-testid="pronounce-word-button"
                    className="rounded-full shrink-0"
                    aria-label="Pronounce word"
                  >
                    <Volume2 className="w-4 h-4" strokeWidth={1.75} />
                  </Button>
                </div>
                <div className="text-lg" data-testid="lesson-translation">→ {lesson.translation}</div>
              </div>

              {/* Example */}
              <div className="border-l-2 border-terracotta pl-4 space-y-1">
                <div className="text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} /> In a sentence
                </div>
                <div className="font-medium" data-testid="lesson-example">&ldquo;{lesson.example_sentence}&rdquo;</div>
                <div className="text-sm text-muted-foreground">{lesson.example_translation}</div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => speak(lesson.example_sentence, language)}
                  className="h-7 px-2 mt-1 -ml-2"
                  data-testid="pronounce-example-button"
                >
                  <Volume2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.75} /> Hear it
                </Button>
              </div>

              {/* Cultural note */}
              <div className="bg-muted rounded-xl p-4 space-y-1">
                <div className="text-sm text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} /> Culture note
                </div>
                <p className="text-sm leading-relaxed" data-testid="lesson-culture">{lesson.cultural_note}</p>
              </div>

              {/* Quiz */}
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground uppercase tracking-widest">Quick check</div>
                <div className="font-medium" data-testid="quiz-question">{lesson.quiz.question}</div>
                <div className="grid grid-cols-1 gap-2" data-testid="quiz-options">
                  {lesson.quiz.options.map((opt, i) => {
                    const isSel = selectedOpt === i;
                    const showRight = submitted && i === correctIdx;
                    const showWrong = submitted && isSel && i !== correctIdx;
                    return (
                      <button
                        key={i}
                        disabled={submitted}
                        onClick={() => setSelectedOpt(i)}
                        data-testid={`quiz-option-${i}`}
                        className={[
                          "text-left px-4 py-3 rounded-xl border transition-colors flex items-center justify-between",
                          !submitted && isSel ? "border-terracotta bg-terracotta/5" : "border-border bg-card",
                          showRight ? "border-forest bg-forest/10 text-forest" : "",
                          showWrong ? "border-destructive bg-destructive/10 text-destructive" : "",
                          !submitted ? "hover:border-terracotta/60" : "",
                        ].join(" ")}
                      >
                        <span>{opt}</span>
                        {showRight && <Check className="w-4 h-4" strokeWidth={2.5} />}
                        {showWrong && <X className="w-4 h-4" strokeWidth={2.5} />}
                      </button>
                    );
                  })}
                </div>
                {!submitted ? (
                  <Button
                    onClick={submitQuiz} disabled={selectedOpt == null}
                    data-testid="submit-quiz-button"
                    className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white"
                  >
                    Check answer
                  </Button>
                ) : (
                  <div
                    className={`text-sm ${isCorrect ? "text-forest" : "text-destructive"}`}
                    data-testid="quiz-feedback"
                  >
                    {isCorrect ? "Nailed it — that's the one." : `Not quite. The answer is: ${lesson.quiz.options[correctIdx]}`}
                  </div>
                )}
              </div>

              {/* Complete */}
              <div className="pt-2 border-t border-border">
                {isFuture ? (
                  <div className="text-sm text-muted-foreground text-center">
                    This lesson lands on {readableDate}. Come back then to log it.
                  </div>
                ) : isCompleted ? (
                  <div className="text-sm text-forest text-center flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" /> Already logged for this day
                  </div>
                ) : (
                  <Button
                    onClick={markComplete}
                    disabled={completing || !submitted}
                    data-testid="mark-complete-button"
                    className="w-full rounded-full bg-forest hover:bg-forest/90 text-white"
                  >
                    {completing ? "Saving…" : submitted ? "Log this day" : "Answer the quiz to log this day"}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
